// --- File type detection ---

export interface FileTypeInfo {
   isImage: boolean;
   isPdf: boolean;
   isDocx: boolean;
   isExcel: boolean;
   isPptx: boolean;
   isAudio: boolean;
   isVideo: boolean;
   /** Plain-text families: text/*, json, xml, yaml, common source-code types. */
   isText: boolean;
}

const TEXT_MIME_PREFIXES = ["text/"];
const TEXT_EXACT_MIMES = new Set([
   "application/json",
   "application/ld+json",
   "application/xml",
   "application/x-yaml",
   "application/yaml",
   "application/x-sh",
   "application/javascript",
   "application/x-javascript",
   "application/typescript",
   "application/x-typescript",
   "application/x-python",
   "application/sql",
]);

const isTextMime = (mimeType: string): boolean => {
   if (TEXT_EXACT_MIMES.has(mimeType)) return true;
   return TEXT_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix));
};

export const detectFileType = (mimeType: string): FileTypeInfo => ({
   isImage: mimeType.startsWith("image/"),
   isPdf: mimeType === "application/pdf",
   isDocx: mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
   isExcel:
      mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      mimeType === "application/vnd.ms-excel",
   isPptx:
      mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
      mimeType === "application/vnd.ms-powerpoint",
   isAudio: mimeType.startsWith("audio/"),
   isVideo: mimeType.startsWith("video/"),
   isText: isTextMime(mimeType),
});

// --- Helpers ---

/** Some generated HTML files arrive with a generic download MIME type. */
export const isHtmlFile = (mimeType: string, fileName: string): boolean => {
   const mime = mimeType.split(";")[0].trim().toLowerCase();
   return mime === "text/html" || mime === "application/xhtml+xml" || /\.html?$/i.test(fileName);
};

/** Decode a base64 string into a Uint8Array. */
export const base64ToBytes = (base64: string): Uint8Array => {
   return Uint8Array.from(Buffer.from(base64, "base64"));
};
